/**
 * VÉRAULT Portal — Advisor: Trips
 *
 * Replatformed (not ported) trip workspace. Editorial list with:
 *  - segmented status filter (Active / Booking / Booked / Past)
 *  - quiet AI assist: "Open the studio" — opens a per-trip lighthouse later
 *  - hover micro-motion + gold cue
 * Rows link to the existing /advisor/trips/:tripId detail page for now
 * (the trip-detail rebuild is a separate dedicated effort).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Search, Sparkles } from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";
import NewJourneyModal from "../../components/NewJourneyModal";

const { fadeInUp, stagger } = portalMotion;

const FILTERS = [
  { id: "active", label: "Active", match: (t) => !["completed", "cancelled", "archived"].includes((t.lifecycle_stage || "").toLowerCase()) },
  { id: "planning", label: "Planning", match: (t) => ["planning_initiated", "details_gathering", "proposing", "planning"].includes((t.lifecycle_stage || "").toLowerCase()) },
  { id: "booking", label: "Booking", match: (t) => ["approved", "booking_in_progress", "in_progress"].includes((t.lifecycle_stage || "").toLowerCase()) },
  { id: "booked", label: "Booked", match: (t) => (t.lifecycle_stage || "").toLowerCase() === "booked" },
  { id: "past", label: "Past", match: (t) => (t.lifecycle_stage || "").toLowerCase() === "completed" },
  { id: "all", label: "All", match: () => true },
];

function stageTone(stage) {
  const s = (stage || "").toLowerCase();
  if (s.includes("complete") || s === "booked") return "sage";
  if (s.includes("cancel") || s.includes("changes")) return "terracotta";
  if (s === "approved" || s === "booking_in_progress") return "gold";
  return "neutral";
}

function humanStage(stage) {
  return (stage || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function AdvisorTrips() {
  const [trips, setTrips] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState("active");
  const [q, setQ] = React.useState("");
  const [newJourneyOpen, setNewJourneyOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/travels/trips", { params: { sort_by: "date" } })
      .then(({ data }) => alive && setTrips(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load trips."));
    return () => {
      alive = false;
    };
  }, []);

  const filtered = React.useMemo(() => {
    if (!trips) return null;
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    let result = trips.filter(f.match);
    if (q.trim()) {
      const needle = q.toLowerCase();
      result = result.filter((t) =>
        [t.internal_trip_name, t.client_trip_title, t.destinations, t.client_name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    return result;
  }, [trips, filter, q]);

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
      <motion.div variants={fadeInUp} className="flex items-start justify-between gap-6 flex-wrap">
        <PageHeader
          eyebrow="Experiences"
          title="Experiences in motion"
          lede="Every itinerary you're shaping, ordered by the next departure."
        />
        <PortalButton
          variant="gold"
          size="md"
          icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}
          onClick={() => setNewJourneyOpen(true)}
          data-testid="trips-begin-journey-btn"
        >
          Begin an experience
        </PortalButton>
      </motion.div>

      <motion.div variants={fadeInUp} className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
        <nav className="flex flex-wrap gap-1" data-testid="trips-filters">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`trips-filter-${f.id}`}
              className={`group relative px-3 py-1.5 font-body text-[13px] tracking-tight transition-colors duration-200 ${
                filter === f.id ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
              }`}
            >
              <span className={filter === f.id ? "font-serif" : ""}>{f.label}</span>
              <span
                className={`absolute -bottom-px left-3 right-3 h-px transition-colors duration-300 ${
                  filter === f.id ? "bg-[hsl(var(--accent-gold))]" : "bg-transparent"
                }`}
                aria-hidden
              />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 max-w-sm rounded-full bg-white/60 border border-[rgba(0,0,0,0.06)] px-4 h-10">
          <Search className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by destination or client…"
            data-testid="trips-search"
            className="flex-1 bg-transparent font-body text-[14px] focus:outline-none placeholder-[hsl(var(--foreground-muted))]"
          />
        </div>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <EditorialRule label={filtered ? `${filtered.length} experience${filtered.length === 1 ? "" : "s"}` : "loading"} />
        <div className="mt-5">
          {error ? (
            <Empty title={error} icon={<Plane className="size-6" strokeWidth={1.25} />} />
          ) : !filtered ? (
            <div className="space-y-2">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Empty title="The studio is quiet" hint="No experiences match this view yet." icon={<Plane className="size-6" strokeWidth={1.25} />} />
          ) : (
            <Surface variant="default" padded={false} className="overflow-hidden">
              <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                {filtered.map((t) => {
                  const start = t.trip_start_date || t.start_date;
                  const end = t.trip_end_date || t.end_date;
                  const days = fmt.daysUntil(start);
                  return (
                    <li key={t.id}>
                      <Link
                        to={`/portal/advisor/journeys/${t.id}`}
                        className="group grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_180px_140px_auto] items-center gap-3 sm:gap-6 px-5 sm:px-6 py-4 hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
                        data-testid={`trip-row-${t.id}`}
                      >
                        <div className="min-w-0">
                          <p className="font-serif text-lg text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--accent-gold))] transition-colors truncate">
                            {t.internal_trip_name || t.client_trip_title || t.destinations || "Untitled"}
                          </p>
                          <p className="mt-0.5 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))] truncate">
                            {[t.client_name, t.destinations].filter(Boolean).join(" · ") || "—"}
                          </p>
                        </div>
                        <div className="hidden sm:block font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                          {fmt.formatDateRange(start, end) || "Dates TBD"}
                          {days != null && days > 0 && days < 90 ? (
                            <span className="block mt-0.5 text-[hsl(var(--accent-gold))]">{days} day{days === 1 ? "" : "s"} out</span>
                          ) : null}
                        </div>
                        <div className="hidden sm:block">
                          <StatusTag tone={stageTone(t.lifecycle_stage)}>{humanStage(t.lifecycle_stage)}</StatusTag>
                        </div>
                        <span className="font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                          {typeof t.price_total === "number" && t.price_total > 0 ? fmt.money(t.price_total, t.price_currency) : "—"}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Surface>
          )}
        </div>
      </motion.div>

      <NewJourneyModal open={newJourneyOpen} onClose={() => setNewJourneyOpen(false)} />
    </motion.div>
  );
}
